import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PriceListHistory = ({ priceLists, activePriceListId, onSelectPriceList }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isExpired = (priceList) => {
    if (!priceList?.valid_until) return false;
    return new Date(priceList?.valid_until) < new Date();
  };

  const getStatusBadge = (priceList) => {
    if (isExpired(priceList)) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
          Expired
        </span>
      );
    }
    if (priceList?.is_active) {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
        Inactive
      </span>
    );
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
          <Icon name="History" size={20} color="var(--color-secondary)" />
        </div>
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">Price List History</h2>
          <p className="text-sm text-muted-foreground">View and manage all price lists for this customer</p>
        </div>
      </div>

      <div className="space-y-3">
        {priceLists?.map((priceList) => (
          <div
            key={priceList?.id}
            className={`border rounded-lg p-4 transition-smooth ${
              priceList?.id === activePriceListId
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-heading font-semibold text-foreground">{priceList?.name}</h3>
                  {getStatusBadge(priceList)}
                </div>
                {priceList?.description && (
                  <p className="text-sm text-muted-foreground mb-3">{priceList?.description}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Valid From:</span>
                    <p className="font-medium text-foreground">{formatDate(priceList?.valid_from)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Valid Until:</span>
                    <p className="font-medium text-foreground">{formatDate(priceList?.valid_until) || 'No expiry'}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Products:</span>
                    <p className="font-medium text-foreground">{priceList?.price_list_items?.length || 0}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <p className="font-medium text-foreground">{formatDate(priceList?.created_at)}</p>
                  </div>
                </div>
                {priceList?.created_by_user && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    Created by: {priceList?.created_by_user?.name}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {priceList?.id !== activePriceListId && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onSelectPriceList(priceList)}
                    iconName="Edit"
                    iconPosition="left"
                  >
                    Edit
                  </Button>
                )}
                {priceList?.id === activePriceListId && (
                  <span className="text-xs font-medium text-primary px-3 py-1.5 bg-primary/10 rounded-md">
                    Currently Editing
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PriceListHistory;